// src/hooks/useTriggerEngine.js
// ─────────────────────────────────────────────────────────────────────────────
//  React hook that bridges useSyncStore values into the trigger engine.
//  Call this once in a top-level component (e.g. Layout or AppContent).
//
//  It binds all relevant stores so that automation handlers can read/write them.
//  Also exposes `fire(eventType, payload)` for components to trigger events.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useCallback, useRef } from 'react';
import { bindStore, unbindStore, emit } from '@/lib/automation/triggerEngine';
// Side-effect import: registers all handlers on first load
import '@/lib/automation/handlers';

/**
 * Bind a single store to the trigger engine.
 * Call once per store you want handlers to be able to read/write.
 *
 * @param {string} key       — store key (e.g. 'calendarEvents')
 * @param {*}      value     — current React state value
 * @param {Function} setter  — the setState / setValue function
 */
export function useBindStore(key, value, setter) {
  // Keep a ref to the latest value so the getter always returns current data
  const valueRef = useRef(value);
  valueRef.current = value;

  useEffect(() => {
    bindStore(key, () => valueRef.current, setter);
    return () => unbindStore(key);
  }, [key, setter]);
}

/**
 * Returns the `fire` function to emit trigger events.
 * Stable reference — safe to pass as a prop or put in a dep array.
 */
export function useTriggerEmit() {
  return useCallback((eventType, payload) => {
    emit(eventType, payload);
  }, []);
}